import type { CardAuthorizationOptions } from '../interfaces/cardPayment.interface';
import { settleConfirm } from '../utils/settleConfirm';
import { buildThreeDsAdditionalData } from '../utils/threeDsAdditionalData';
import { hyperswitchFailureCode } from '../shared/utils/hyperswitchRail';

export function useCardAuthorization(options: CardAuthorizationOptions) {
  const store = useKegelAnalyticStore();
  const { rememberMethod } = useHyperswitchRedirectReturn();
  const { paymentIntent, paymentComponent, nameOnCard } = options;

  const isRedirecting = ref(false);

  async function authorizeHyperswitchCard(): Promise<void> {
    rememberMethod('card');

    const outcome = await settleConfirm(paymentComponent.value.confirm({ cardholderName: nameOnCard.value }));

    if (outcome.kind === 'authorized') {
      paymentIntent.value = {
        ...paymentIntent.value,
        id: outcome.paymentId || paymentIntent.value?.id,
        status: outcome.raw?.status,
      };

      return;
    }

    if (outcome.kind === 'requires-action' && outcome.redirectUrl) {
      isRedirecting.value = true;
      globalThis.location.assign(outcome.redirectUrl);
      return;
    }

    const refusal = outcome.kind === 'declined' || outcome.kind === 'intent-unusable' ? outcome : null;

    paymentIntent.value = { ...paymentIntent.value, status: '' };
    const code = hyperswitchFailureCode(outcome.kind, refusal?.code);
    await options.onCardError(null, `${refusal?.message ?? 'Payment failed'} code:${code}`);
  }

  async function authorizeLagoCard(): Promise<void> {
    const fields = { name: nameOnCard.value };
    const handlers = { error: options.onCardError };

    if (paymentIntent.value?.intent_type === 'setup_intent') {
      paymentIntent.value = await paymentComponent.value.confirmSetup(paymentIntent.value, fields, handlers);

      return;
    }

    paymentIntent.value = await paymentComponent.value.authorizeWith3ds(paymentIntent.value, fields, handlers);
  }

  async function authorize(): Promise<void> {
    if (options.isHyperswitch.value) {
      await authorizeHyperswitchCard();

      return;
    }

    if (options.isLago.value) {
      await authorizeLagoCard();

      return;
    }

    if (paymentIntent.value?.status !== 'inited') return;

    paymentIntent.value = await paymentComponent.value.authorizeWith3ds(
      paymentIntent.value,
      buildThreeDsAdditionalData(nameOnCard.value, store.userEmail),
      { error: options.onCardError },
    );
  }

  return { authorize, isRedirecting };
}
